/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  context,
  propagation,
  SpanKind,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api';
import type { Attributes, HrTime, Span } from '@opentelemetry/api';
import type { InstrumentationConfig } from '@opentelemetry/instrumentation';
import {
  InstrumentationBase,
  safeExecuteInTheMiddle,
} from '@opentelemetry/instrumentation';
import * as core from '@opentelemetry/core';
import * as web from '@opentelemetry/web-common';
import { ATTR_HTTP_REQUEST_BODY_SIZE } from './semconv';
import {
  ATTR_ERROR_TYPE,
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_HTTP_REQUEST_METHOD_ORIGINAL,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_SERVER_ADDRESS,
  ATTR_SERVER_PORT,
  ATTR_URL_FULL,
} from '@opentelemetry/semantic-conventions';
import type { FetchError, FetchResponse, SpanData } from './types';
import {
  getFetchBodyLength,
  normalizeHttpRequestMethod,
  serverPortFromUrl,
} from './utils';
import { VERSION } from './version';

// how long to wait for observer to collect information about resources
// this is needed as event "load" is called before observer
// hard to say how long it should really wait, seems like 300ms is
// safe enough
const OBSERVER_WAIT_TIME_MS = 300;

const hasBrowserPerformanceAPI = typeof PerformanceObserver !== 'undefined';

export interface FetchCustomAttributeFunction {
  (
    span: Span,
    request: Request | RequestInit,
    result: Response | FetchError
  ): void;
}

export interface FetchRequestHookFunction {
  (span: Span, request: Request | RequestInit): void;
}

/**
 * FetchPlugin Config
 */
export interface FetchInstrumentationConfig extends InstrumentationConfig {
  // the number of timing resources is limited, after the limit
  // (chrome 250, safari 150) the information is not collected anymore
  // the only way to prevent that is to regularly clean the resources
  // whenever it is possible, this is needed only when PerformanceObserver
  // is not available
  clearTimingResources?: boolean;
  // urls which should include trace headers when origin doesn't match
  propagateTraceHeaderCorsUrls?: web.PropagateTraceHeaderCorsUrls;
  /**
   * URLs that partially match any regex in ignoreUrls will not be traced.
   * In addition, URLs that are _exact matches_ of strings in ignoreUrls will
   * also not be traced.
   */
  ignoreUrls?: Array<string | RegExp>;
  /** Function for adding custom attributes on the span */
  applyCustomAttributesOnSpan?: FetchCustomAttributeFunction;
  /** Function for adding custom attributes or headers before the request is handled */
  requestHook?: FetchRequestHookFunction;
  // Ignore adding network events as span events
  ignoreNetworkEvents?: boolean;
  /** Measure outgoing request size */
  measureRequestSize?: boolean;
}

/**
 * This class represents a fetch plugin for auto instrumentation
 */
export class FetchInstrumentation extends InstrumentationBase<FetchInstrumentationConfig> {
  readonly component: string = 'fetch';
  readonly version: string = VERSION;
  moduleName = this.component;
  private _usedResources = new WeakSet<PerformanceResourceTiming>();
  private _tasksCount = 0;

  // Note: Intentionally *not* using `_enabled` as the field name to avoid
  // any possible confusion with the `_enabled` field used on the *Node.js*
  // InstrumentationBase class.
  // Also not initializing the fields to `false` because the base class
  // constructor already call `enable` modifying their values and it will
  // set the instrumentaitons in a bas state (enabled, patched but with flags set to false)
  declare private _isEnabled: boolean;
  declare private _isFetchPatched: boolean;

  constructor(config: FetchInstrumentationConfig = {}) {
    super('@opentelemetry/instrumentation-fetch', VERSION, config);
  }

  init(): void {}

  /**
   * Add cors pre flight child span
   * @param span
   * @param corsPreFlightRequest
   */
  private _addChildSpan(
    span: Span,
    corsPreFlightRequest: PerformanceResourceTiming
  ): void {
    const childSpan = this.tracer.startSpan(
      'CORS Preflight',
      {
        startTime: corsPreFlightRequest[web.PerformanceTimingNames.FETCH_START],
      },
      trace.setSpan(context.active(), span)
    );
    web.addSpanNetworkEvents(
      childSpan,
      corsPreFlightRequest,
      this.getConfig().ignoreNetworkEvents,
      undefined
    );
    childSpan.end(
      corsPreFlightRequest[web.PerformanceTimingNames.RESPONSE_END]
    );
  }

  /**
   * Adds more attributes to span just before ending it
   * @param span
   * @param response
   */
  private _addFinalSpanAttributes(span: Span, response: FetchResponse): void {
    const parsedUrl = web.parseUrl(response.url);
    span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, response.status);
    // TODO: Set server.{address,port} at span creation for sampling decisions
    // (a "SHOULD" requirement in semconv).
    span.setAttribute(ATTR_SERVER_ADDRESS, parsedUrl.hostname);
    const serverPort = serverPortFromUrl(parsedUrl);
    if (serverPort) {
      span.setAttribute(ATTR_SERVER_PORT, serverPort);
    }
  }

  /**
   * Add headers
   * @param options
   * @param spanUrl
   */
  private _addHeaders(options: Request | RequestInit, spanUrl: string): void {
    if (
      !web.shouldPropagateTraceHeaders(
        spanUrl,
        this.getConfig().propagateTraceHeaderCorsUrls
      )
    ) {
      const headers: Partial<Record<string, unknown>> = {};
      propagation.inject(context.active(), headers);
      if (Object.keys(headers).length > 0) {
        this._diag.debug('headers inject skipped due to CORS policy');
      }
      return;
    }

    if (options instanceof Request) {
      // This mutates `Request.headers` in-place, because it is read-only
      // (per https://developer.mozilla.org/en-US/docs/Web/API/Request/headers),
      // so we cannot (easily) replace it.
      propagation.inject(context.active(), options.headers, {
        set: (h, k, v) => h.set(k, typeof v === 'string' ? v : String(v)),
      });
    } else {
      // Otherwise, create a new Headers to avoid mutating the caller's
      // possibly re-used headers.
      const headers = new Headers(options.headers);
      propagation.inject(context.active(), headers, {
        set: (h, k, v) => h.set(k, typeof v === 'string' ? v : String(v)),
      });
      options.headers = headers;
    }
  }

  /**
   * Clears the resource timings and all resources assigned with spans
   *     when {@link FetchPluginConfig.clearTimingResources} is
   *     set to true (default false)
   * @private
   */
  private _clearResources() {
    if (this._tasksCount === 0 && this.getConfig().clearTimingResources) {
      performance.clearResourceTimings();
      this._usedResources = new WeakSet<PerformanceResourceTiming>();
    }
  }

  /**
   * Creates a new span
   * @param url
   * @param options
   */
  private _createSpan(
    url: string,
    options: Partial<Request | RequestInit> = {}
  ): Span | undefined {
    if (core.isUrlIgnored(url, this.getConfig().ignoreUrls)) {
      this._diag.debug('ignoring span as url matches ignored url');
      return;
    }

    const attributes = {} as Attributes;
    const origMethod = options.method;
    const normMethod = normalizeHttpRequestMethod(options.method || 'GET');
    const name = normMethod;

    attributes[ATTR_HTTP_REQUEST_METHOD] = normMethod;
    if (normMethod !== origMethod) {
      attributes[ATTR_HTTP_REQUEST_METHOD_ORIGINAL] = origMethod;
    }
    attributes[ATTR_URL_FULL] = url;

    return this.tracer.startSpan(name, {
      kind: SpanKind.CLIENT,
      attributes,
    });
  }

  /**
   * Finds appropriate resource and add network events to the span
   * @param span
   * @param resourcesObserver
   * @param endTime
   */
  private _findResourceAndAddNetworkEvents(
    span: Span,
    resourcesObserver: SpanData,
    endTime: HrTime
  ): void {
    let resources: PerformanceResourceTiming[] = resourcesObserver.entries;
    if (!resources.length) {
      if (!performance.getEntriesByType) {
        return;
      }
      // fallback - either Observer is not available or it took longer
      // then OBSERVER_WAIT_TIME_MS and observer didn't collect enough
      // information
      resources = performance.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];
    }
    const resource = web.getResource(
      resourcesObserver.spanUrl,
      resourcesObserver.startTime,
      endTime,
      resources,
      this._usedResources,
      'fetch'
    );

    if (resource.mainRequest) {
      const mainRequest = resource.mainRequest;
      this._markResourceAsUsed(mainRequest);

      const corsPreFlightRequest = resource.corsPreFlightRequest;
      if (corsPreFlightRequest) {
        this._addChildSpan(span, corsPreFlightRequest);
        this._markResourceAsUsed(corsPreFlightRequest);
      }
      web.addSpanNetworkEvents(
        span,
        mainRequest,
        this.getConfig().ignoreNetworkEvents,
        undefined
      );
    }
  }

  /**
   * Marks certain [resource]{@link PerformanceResourceTiming} when information
   * from this is used to add events to span.
   * This is done to avoid reusing the same resource again for next span
   * @param resource
   */
  private _markResourceAsUsed(resource: PerformanceResourceTiming): void {
    this._usedResources.add(resource);
  }

  /**
   * Finish span, add attributes, network events etc.
   * @param span
   * @param spanData
   * @param response
   */
  private _endSpan(span: Span, spanData: SpanData, response: FetchResponse) {
    const endTime = core.millisToHrTime(Date.now());
    const performanceEndTime = core.hrTime();
    this._addFinalSpanAttributes(span, response);

    // https://github.com/open-telemetry/semantic-conventions/blob/main/docs/http/http-spans.md#status
    if (response.status >= 400) {
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.setAttribute(ATTR_ERROR_TYPE, String(response.status));
    }

    setTimeout(() => {
      spanData.observer?.disconnect();
      this._findResourceAndAddNetworkEvents(span, spanData, performanceEndTime);
      this._tasksCount--;
      this._clearResources();
      span.end(endTime);
    }, OBSERVER_WAIT_TIME_MS);
  }

  /**
   * Patches the constructor of fetch
   */
  private _patchConstructor(): (original: typeof fetch) => typeof fetch {
    return original => {
      const plugin = this;

      return function patchConstructor(
        this: typeof globalThis,
        ...args: Parameters<typeof fetch>
      ): Promise<Response> {
        if (!plugin._isEnabled) {
          return original.apply(this, args);
        }
        const self = this;
        const url = web.parseUrl(
          args[0] instanceof Request ? args[0].url : String(args[0])
        ).href;

        // Per the Fetch spec, when fetch() is called with a Request object
        // and a separate init object, the init properties override the
        // Request's properties. Merge them into a new Request so that
        // downstream consumers (hooks, header injection, the actual fetch
        // call) see the correct final values.
        // See: https://developer.mozilla.org/en-US/docs/Web/API/Request/Request#parameters
        let options: Request | RequestInit;
        if (args[0] instanceof Request) {
          options = args[1] != null ? new Request(args[0], args[1]) : args[0];
        } else {
          options = args[1] || {};
        }
        const createdSpan = plugin._createSpan(url, options);
        if (!createdSpan) {
          return original.apply(this, args);
        }
        const spanData = plugin._prepareSpanData(url);

        if (plugin.getConfig().measureRequestSize) {
          getFetchBodyLength(...args)
            .then(bodyLength => {
              if (!bodyLength) return;
              createdSpan.setAttribute(ATTR_HTTP_REQUEST_BODY_SIZE, bodyLength);
            })
            .catch(error => {
              plugin._diag.warn('getFetchBodyLength', error);
            });
        }

        function endSpanOnError(span: Span, error: FetchError) {
          plugin._applyAttributesAfterFetch(span, options, error);
          plugin._endSpan(span, spanData, {
            status: error.status || 0,
            statusText: error.message,
            url,
          });
        }

        function endSpanOnSuccess(span: Span, response: Response) {
          plugin._applyAttributesAfterFetch(span, options, response);
          if (response.status >= 200 && response.status < 400) {
            plugin._endSpan(span, spanData, response);
          } else {
            plugin._endSpan(span, spanData, {
              status: response.status,
              statusText: response.statusText,
              url,
            });
          }
        }

        function onSuccess(span: Span, response: Response): Response {
          try {
            // Clone the response and eagerly consume the clone to detect
            // when the body transfer completes.  The original response is
            // returned to the caller untouched so that it passes internal
            // brand-checks required by APIs such as
            // WebAssembly.compileStreaming.
            // TODO: Switch to a consumer-driven model and drop `resClone`.
            // Keeping eager consumption here to preserve current behavior and avoid breaking existing tests.
            // Context: discussion in PR #5894 → https://github.com/open-telemetry/opentelemetry-js/pull/5894
            const resClone = response.clone();
            const body = resClone.body;
            if (body) {
              const reader = body.getReader();

              const read = (): void => {
                reader.read().then(
                  ({ done }) => {
                    if (done) {
                      endSpanOnSuccess(span, response);
                    } else {
                      read();
                    }
                  },
                  error => {
                    endSpanOnError(span, error);
                  }
                );
              };
              read();
            } else {
              // some older browsers don't have .body implemented
              endSpanOnSuccess(span, response);
            }
          } catch (error) {
            // Setup failed (e.g. clone() or getReader() threw).
            // End the span and clean up so _tasksCount doesn't leak.
            plugin._diag.error('Failed to read fetch response body', error);
            plugin._endSpan(span, spanData, {
              status: 0,
              url,
            });
          }
          return response;
        }

        function onError(span: Span, error: FetchError): never {
          try {
            endSpanOnError(span, error);
          } catch (e: unknown) {
            // endSpanOnError failed — fall back to ending the span
            // directly so _tasksCount doesn't leak.
            plugin._diag.error('Failed to end span on fetch error', e);
            plugin._endSpan(span, spanData, {
              status: error.status || 0,
              url,
            });
          }

          throw error;
        }

        return context.with(
          trace.setSpan(context.active(), createdSpan),
          () => {
            // Call request hook before injection so hooks cannot tamper with propagation headers.
            // Also, this means the hook will see `options.headers` in the same type as passed in,
            // rather than as a `Headers` instance set by `_addHeaders()`.
            plugin._callRequestHook(createdSpan, options);
            plugin._addHeaders(options, url);
            plugin._tasksCount++;

            return original
              .apply(
                self,
                options instanceof Request ? [options] : [url, options]
              )
              .then(
                onSuccess.bind(self, createdSpan),
                onError.bind(self, createdSpan)
              );
          }
        );
      };
    };
  }

  private _applyAttributesAfterFetch(
    span: Span,
    request: Request | RequestInit,
    result: Response | FetchError
  ) {
    const applyCustomAttributesOnSpan =
      this.getConfig().applyCustomAttributesOnSpan;
    if (applyCustomAttributesOnSpan) {
      safeExecuteInTheMiddle(
        () => applyCustomAttributesOnSpan(span, request, result),
        error => {
          if (!error) {
            return;
          }

          this._diag.error('applyCustomAttributesOnSpan', error);
        },
        true
      );
    }
  }

  private _callRequestHook(span: Span, request: Request | RequestInit) {
    const requestHook = this.getConfig().requestHook;
    if (requestHook) {
      safeExecuteInTheMiddle(
        () => requestHook(span, request),
        error => {
          if (!error) {
            return;
          }

          this._diag.error('requestHook', error);
        },
        true
      );
    }
  }

  /**
   * Prepares a span data - needed later for matching appropriate network
   *     resources
   * @param spanUrl
   */
  private _prepareSpanData(spanUrl: string): SpanData {
    const startTime = core.hrTime();
    const entries: PerformanceResourceTiming[] = [];
    if (typeof PerformanceObserver !== 'function') {
      return { entries, startTime, spanUrl };
    }

    const observer = new PerformanceObserver(list => {
      const perfObsEntries = list.getEntries() as PerformanceResourceTiming[];
      perfObsEntries.forEach(entry => {
        if (entry.initiatorType === 'fetch' && entry.name === spanUrl) {
          entries.push(entry);
        }
      });
    });
    observer.observe({
      entryTypes: ['resource'],
    });
    return { entries, observer, startTime, spanUrl };
  }

  /**
   * implements enable function
   */
  override enable(): void {
    if (!hasBrowserPerformanceAPI) {
      this._diag.warn(
        'this instrumentation is intended for web usage only, it does not instrument server-side fetch()'
      );
      return;
    }

    if (this._isEnabled) {
      return;
    }

    if (this._isFetchPatched) {
      this._diag.debug('fetch constructor already patched');
      this._isEnabled = true;
      return;
    }

    try {
      // `_wrap` throws if a third-party script has locked globalThis.fetch via
      // Object.defineProperty(window, 'fetch', { writable: false, ... }).
      this._wrap(globalThis, 'fetch', this._patchConstructor());
      this._isFetchPatched = true;
      this._isEnabled = true;
    } catch (err) {
      this._diag.warn(
        'Failed to patch globalThis.fetch; instrumentation will not be enabled. ' +
          'Another script may have locked globalThis.fetch via Object.defineProperty.',
        err
      );
    }
  }

  /**
   * deactivates fetch instrumentation
   */
  override disable(): void {
    if (!hasBrowserPerformanceAPI) {
      return;
    }
    if (!this._isEnabled) {
      return;
    }
    this._isEnabled = false;
    this._usedResources = new WeakSet<PerformanceResourceTiming>();
  }
}
